import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import AddAppointmentDialog from '../components/AddAppointmentDialog';
import AddClientDialog from '../components/AddClientDialog';
import PageHelp from '../components/help/PageHelp';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Users, Search, Loader2, Phone, Mail, Calendar, ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/dashboard/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client => {
    const search = searchTerm.toLowerCase();
    return (
      client.clientName?.toLowerCase().includes(search) ||
      client.clientPhone?.toLowerCase().includes(search) ||
      client.clientEmail?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="clients-title">
              <Users className="h-6 w-6" />
              Clients
              <PageHelp
                testId="clients"
                title="Clients"
                purpose="Everyone you've booked is saved here with their history and animals."
                actions={[
                  { label: 'Add Client', desc: 'save a new client by hand' },
                  { label: 'Add Appointment', desc: 'log a job for a client' },
                  { label: 'Tap a client', desc: 'see their bookings, animals, and your private notes' },
                ]}
                nextStep="Open a client to add private notes only you can see."
              />
            </h1>
            <p className="text-muted-foreground">
              {clients.length} client{clients.length !== 1 ? 's' : ''} total
            </p>
          </div>
          {/* Session 27 — quick-add appointment (auto-refreshes client list
              when the dialog quick-creates a new CRM client). */}
          <div className="shrink-0 flex flex-wrap gap-2" data-testid="clients-actions">
            <AddClientDialog onCreated={loadClients} />
            <AddAppointmentDialog onCreated={loadClients} />
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clients by name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="client-search"
          />
        </div>

        {/* Client List */}
        {clients.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No clients yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Clients will appear here once you receive bookings — or use{' '}
                <span className="font-medium">Add Appointment</span> above to log one manually.
              </p>
            </CardContent>
          </Card>
        ) : filteredClients.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No clients match your search</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4" data-testid="clients-list">
            {filteredClients.map((client) => (
              <Card 
                key={client.id} 
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => navigate(`/dashboard/clients/${client.id}`)}
                data-testid={`client-card-${client.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-lg truncate">
                          {client.clientName}
                        </h3>
                        {client.horseRecordCount > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {client.horseRecordCount} horse{client.horseRecordCount !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <span>{client.clientPhone}</span>
                        </div>
                        {client.clientEmail && (
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            <span className="truncate max-w-[200px]">{client.clientEmail}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                        <span className="text-muted-foreground">
                          <strong>{client.totalBookings}</strong> booking{client.totalBookings !== 1 ? 's' : ''}
                        </span>
                        <span className="text-muted-foreground">
                          <strong>{client.totalHorses}</strong> horse{client.totalHorses !== 1 ? 's' : ''} serviced
                        </span>
                        {client.lastAppointment && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            Last: {format(new Date(client.lastAppointment), 'MMM d, yyyy')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
